import type { IAvailability, ICalendarAvailability } from "@app/availability/types";
import type { IExtra } from "@app/common/types";
import { availableDateRange } from "@app/common/utils/calendarUtils";
import type { IProperty } from "@app/property/types";
import { UtilsIdentifier } from "@hotelchamp/common";

import { getInitialFormState } from "../../context/BookingEngineStateProvider";
import { IBookingEngineState } from "../../types";
import { getRateByShortId } from "../booking/getRateByShortId";
import { getSelectedExtras } from "../booking/getSelectedExtras";
import { matchShortId } from "../ids/generateIds";
import { resolveValidLanguage } from "../languageDetector";
import { urlParamsToFilterState } from "./urlParamsToFilterState";
import { validUrlParams } from "./validUrlParams";

export const urlParamsToState = (
    params: string,
    property: IProperty | undefined,
    availability: IAvailability[],
    extras: IExtra[][],
    calendarAvailability: ICalendarAvailability
): IBookingEngineState | null => {
    const q = new URLSearchParams(params);

    if (!property) {
        return null;
    }

    if (!availability.length && !params.includes("id=")) {
        return null;
    }

    const initialState = getInitialFormState(property);

    if (!validUrlParams(q)) {
        return initialState;
    }

    const [fromDate, toDate] = (q.get("d") || "").split(",");

    const availableDates = calendarAvailability ? availableDateRange(calendarAvailability, fromDate, toDate) : true;
    const language = resolveValidLanguage(q.get("lg") || "", property?.languages, initialState.language).resolvedLanguage;

    const state: IBookingEngineState = {
        ...initialState,
        bookableItemsFilter: urlParamsToFilterState(q, property),
        language,
        currency: q.get("cu") || initialState.currency,
        promoCode: q.get("pc") || initialState.promoCode,
        corpCode: q.get("cc") || initialState.corpCode,
        bookingState: {
            bookings: initialState.bookingState?.bookings || [],
            dates: { fromDate, toDate },
        },
        bookingId: q.get("id") || undefined,
        activeStep: q.get("c") || initialState.activeStep,
        activeWarningKey: !availableDates ? "date" : null,
    };

    // Process the new single 'br' parameter with semicolon-separated bookings
    const brValue = q.get("br");

    if (brValue) {
        const bookingsData = brValue.split(";");
        bookingsData.forEach((br, brIndex) => {
            const [bookings, extraIds] = br.split("|");
            const props = (bookings || "").split(",");
            const type = props[1] === "_" ? "room" : "package";
            const foundPackage = (availability[brIndex]?.packages || []).find((p) => matchShortId(p.id, props[1])) || null;
            const foundRoom = (availability[brIndex]?.rooms || []).find((r) => matchShortId(props[2], r.id)) || null;
            const foundRate = getRateByShortId(type === "room" ? foundRoom : foundPackage, props[3]) || null;
            const selectedExtras = getSelectedExtras(extras[brIndex], (extraIds || "").split(","));

            const childCount: { [categoryId: string]: number } = {};

            const childCounts = props.slice(5);
            childCounts.forEach((countStr) => {
                const [categoryId, count] = countStr.split(":");
                childCount[categoryId] = Number(count);
            });

            state.bookingState.bookings[brIndex] = {
                id: props[0] === "_" ? UtilsIdentifier.uniqueString() : props[0],
                type,
                package: foundPackage,
                room: foundRoom,
                rate: foundRate,
                adultsCount: Number(props[4]),
                childCount,
                isConfirmed: Number(props[7]) === 1 ? true : false,
                extras: selectedExtras,
                price: undefined,
                discountedPrice: undefined,
            };

            const roomWarning = props[2] !== "_" && !foundRoom;
            const rateWarning = props[3] !== "_" && !foundRate;

            state.activeWarningKey = roomWarning ? "room" : rateWarning ? "rate" : state.activeWarningKey;
        });
    }

    // q.getAll("br").forEach((br, i) => {
    //     const [bookings, extraIds] = br.split("|");
    //     const props = (bookings || "").split(",");
    //     const type = props[1] === "_" ? "room" : "package";
    //     const foundPackage = (availability[i]?.packages || []).find((p) => matchShortId(p.id, props[1])) || null;
    //     const foundRoom = (availability[i]?.rooms || []).find((r) => matchShortId(props[2], r.id)) || null;
    //     const foundRate = getRateByShortId(type === "room" ? foundRoom : foundPackage, props[3]) || null;
    //     const selectedExtras = getSelectedExtras(extras[i], (extraIds || "").split(","));
    //     const childCount: { [categoryId: string]: number } = {};
    //     const childCounts = props.slice(5);
    //     childCounts.forEach((countStr) => {
    //         const [categoryId, count] = countStr.split(":");
    //         childCount[categoryId] = Number(count);
    //     });
    //     state.bookingState.bookings[i] = {
    //         id: props[0] === "_" ? UtilsIdentifier.uniqueString() : props[0],
    //         type,
    //         package: foundPackage,
    //         room: foundRoom,
    //         rate: foundRate,
    //         adultsCount: Number(props[4]),
    //         childCount,
    //         isConfirmed: Number(props[7]) === 1 ? true : false,
    //         extras: selectedExtras,
    //         price: undefined,
    //         discountedPrice: undefined,
    //     };
    //     const roomWarning = props[2] !== "_" && !foundRoom;
    //     const rateWarning = props[3] !== "_" && !foundRate;
    //     state.activeWarningKey = roomWarning ? "room" : rateWarning ? "rate" : state.activeWarningKey;
    // });
    return state;
};
